
/**
 * Module dependencies.
 */

import { updateUser } from 'src/api/app/user/update-user';
import { useAuth } from 'src/context/auth/context';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import {
  userJobTitle,
  userProfessionalType,
  userType
} from 'src/core/user-config';

import Input from 'src/components/core/forms/fields/input';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';

import Select from 'src/components/core/forms/fields/select';
import Sidebar from 'src/components/my-revigres/core/sidebar';
import find from 'lodash/find';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import useAutoFocus from 'src/hooks/use-auto-focus';
import useCountries from 'src/api/app/countries/use-countries';
import useDistricts from 'src/api/app/countries/use-districts';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  isOpen: boolean,
  onClose: () => void
};

/**
 * `useUpdateUser` hook.
 */

function useUpdateUser() {
  const { translate } = useTranslate();
  const { showMessage } = useSnackbar();
  const { token } = useAuth();
  const { isLoading, mutateAsync } = useMutation(updateUser);
  const handleUpdateUser = useCallback(formData => {
    return mutateAsync({ formData, token }, {
      onError: () => {
        showMessage(translate('my-revigres:dashboard.user.updateUser.errorMessage'), {
          appearance: 'error'
        });
      },
      onSuccess: () => {
        showMessage(translate('my-revigres:dashboard.user.updateUser.successMessage'), {
          appearance: 'success'
        });
      }
    });
  }, [mutateAsync, showMessage, token, translate]);

  return {
    isSubmitting: isLoading,
    onUpdateUser: handleUpdateUser
  };
}

/**
 * `UserDataSidebar` component.
 */

const UserDataSidebar = (props: Props): ReactElement => {
  const { isOpen, onClose } = props;
  const { translate } = useTranslate();
  const { updateUser: updateAuthUser, user } = useAuth();
  const userData = user?.user;
  const authUserData = user?.authUser;
  const {
    data: countriesData,
    isLoading: isLoadingCountries,
    isSuccess: isSuccessCountries
  } = useCountries();

  const {
    data: districtsData,
    isLoading: isLoadingDistricts,
    isSuccess: isSuccessDistricts
  } = useDistricts();

  const professionalTypeOptions = sortBy(Object
    .entries(userProfessionalType)
    .map(([key, value]) => ({
      label: translate(`forms:professionalType.${key}`),
      value
    })), option => {
    if (option.value !== userProfessionalType.other) {
      return option.label;
    }
  });

  const jobTitleOptions = sortBy(Object
    .entries(userJobTitle)
    .map(([key, value]) => ({
      label: translate(`forms:jobTitle.${key}`),
      value
    })), option => {
    if (option.value !== userJobTitle.other) {
      return option.label;
    }
  });

  const defaultProfessionalType = useMemo(() => {
    const key = find(Object.keys(userProfessionalType), key => {
      return userProfessionalType[key] === userData?.professionalType;
    });

    return {
      label: translate(`forms:professionalType.${key}`),
      value: userProfessionalType[key]
    };
  }, [translate, userData]);

  const defaultJobTitle = useMemo(() => {
    const key = find(Object.keys(userJobTitle), key => {
      return userJobTitle[key] === userData?.jobTitle;
    });

    return {
      label: translate(`forms:jobTitle.${key}`),
      value: userJobTitle[key]
    };
  }, [translate, userData]);

  const defaultUserData = useMemo(() => {
    return {
      country: {
        label: userData?.country?.name,
        value: userData?.country?.alpha2
      },
      district: {
        label: userData?.district?.name,
        value: userData?.district?.id
      },
      name: userData?.name,
      phoneNumber: userData?.phoneNumber,
      ...userData?.country?.alpha2 !== 'PT' && { location: userData?.location },
      ...userData?.type === userType.reseller && { jobTitle: defaultJobTitle },
      ...userData?.type === userType.professional && { professionalType: defaultProfessionalType },
      ...(userData?.type === userType.professional || userData?.type === userType.reseller) && {
        company: userData?.company
      }
    };
  }, [userData, defaultJobTitle, defaultProfessionalType]);

  const {
    control,
    errors,
    formState,
    handleSubmit,
    register,
    reset,
    watch
  } = useForm({
    defaultValues: defaultUserData,
    mode: 'onBlur'
  });

  const watchCountry = watch('country');
  const { isDirty } = formState;
  const { isSubmitting, onUpdateUser } = useUpdateUser();
  const inputRef = useRef();
  const isLoading = isLoadingCountries || isLoadingDistricts;
  const handleUpdateUserData = useCallback(data => {
    const {
      company,
      country,
      district,
      jobTitle,
      location,
      name,
      phoneNumber,
      professionalType
    } = data;

    const normalizedData = {
      company,
      countryAlpha2: country?.value,
      name,
      ...jobTitle && { jobTitle: jobTitle?.value },
      ...phoneNumber && { phoneNumber },
      ...professionalType && { professionalType: professionalType?.value },
      ...country?.value === 'PT' ? { districtId: district?.value } : { location }
    };

    onUpdateUser(normalizedData)
      .then(() => {
        onClose();
        updateAuthUser();
      });
  }, [onClose, onUpdateUser, updateAuthUser]);

  useAutoFocus({
    isActive: isOpen,
    ref: inputRef
  });

  useEffect(() => {
    reset(defaultUserData);
  }, [defaultUserData, reset, userData]);

  return (
    <Sidebar
      isOpen={isOpen}
      isSubmitDisabled={!isDirty}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={handleSubmit(handleUpdateUserData)}
      title={translate('my-revigres:dashboard.menuOptions.personalData')}
    >
      <Input
        defaultValue={translate(`forms:userType.${userData?.type}`)}
        disabled
        label={translate('forms:userTypeLabel')}
        name={'userType'}
      />

      <Input
        disabled={isLoading}
        error={errors?.name}
        label={translate('forms:nameLabel')}
        name={'name'}
        placeholder={translate('forms:namePlaceholder')}
        ref={element => {
          // @ts-ignore
          inputRef.current = element;
          register<any>(element, {
            required: {
              message: translate('forms:requiredField'),
              value: true
            }
          });
        }}
      />

      <Input
        defaultValue={authUserData?.email}
        disabled
        label={translate('forms:emailLabel')}
        name={'email'}
      />

      {(userData?.type === userType.professional || userData?.type === userType.reseller) && (
        <Input
          disabled={isLoading}
          error={errors?.company}
          label={translate('forms:companyNameLabel')}
          name={'company'}
          placeholder={translate('forms:companyNamePlaceholder')}
          ref={register}
        />
      )}

      {userData?.type === userType.professional && (
        <Select
          control={control}
          disabled={isLoading}
          label={translate('forms:professionLabel')}
          name={'professionalType'}
          options={professionalTypeOptions}
          placeholder={translate('forms:selectPlaceholder')}
          rules={register}
        />
      )}

      {userData?.type === userType.reseller && (
        <Select
          control={control}
          disabled={isLoading}
          label={translate('forms:jobTitleLabel')}
          name={'jobTitle'}
          options={jobTitleOptions}
          placeholder={translate('forms:selectPlaceholder')}
          rules={register}
        />
      )}

      <Input
        disabled={isSubmitting}
        error={errors?.phoneNumber}
        label={translate('forms:phoneNumberLabel')}
        name={'phoneNumber'}
        placeholder={translate('forms:phoneNumberPlaceholder')}
        ref={register}
      />

      {isSuccessCountries && (
        <Select
          control={control}
          disabled={isSubmitting}
          id={'user-data-sidebar-country'}
          isLoading={isLoadingCountries}
          label={translate('forms:countryLabel')}
          name={'country'}
          options={map(countriesData, ({ alpha2, name }) => ({
            label: name,
            value: alpha2
          }))}
          placeholder={translate('forms:selectPlaceholder')}
          rules={{ required: {
            message: translate('forms:requiredField'),
            value: true
          } }}
        />
      )}

      {watchCountry.value === 'PT' ? isSuccessDistricts && (
        <Select
          control={control}
          disabled={isSubmitting}
          isLoading={isLoadingDistricts}
          label={translate('forms:districtLabel')}
          name={'district'}
          options={map(districtsData, ({ id, name }) => ({
            label: name,
            value: id
          }))}
          placeholder={translate('forms:districtPlaceholder')}
          rules={{ required: {
            message: translate('forms:requiredField'),
            value: true
          } }}
        />
      ) : (
        <Input
          disabled={isSubmitting}
          error={errors?.location}
          label={translate('forms:locationLabel')}
          name={'location'}
          placeholder={translate('forms:locationPlaceholder')}
          ref={register<any>({
            required: {
              message: translate('forms:requiredField'),
              value: true
            }
          })}
        />
      )}
    </Sidebar>
  );
};

/**
 * Export `UserDataSidebar` component.
 */

export default UserDataSidebar;
